import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import supabase from '../lib/supabaseClient';
import { API_BASE_URL } from '../config/api';

const ADMIN_API_BASE = `${API_BASE_URL}/api/admin`;
const SUSPENSION_CACHE_KEY = 'adminMoverSuspensionStatus';

const readSuspensionCache = () => {
  if (typeof window === 'undefined') return {};

  try {
    const rawValue = localStorage.getItem(SUSPENSION_CACHE_KEY);
    if (!rawValue) return {};

    const parsedValue = JSON.parse(rawValue);
    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch {
    return {};
  }
};

const writeSuspensionCache = (cache) => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(SUSPENSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
  }
};

const createStableCompletedMoves = (mover) => {
  const seed = `${mover.id || ''}${mover.company_name || ''}`;
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }

  return 100 + (hash % 201);
};

const INVALID_DOC_VALUES = new Set([
  'n/a',
  'na',
  'n\\a',
  'none',
  'null',
  'nil',
  'pending',
  'not provided',
  'not available',
  '-',
  '--',
  '---',
  'tbd',
]);

const hasMeaningfulDocumentValue = (value) => {
  if (value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return false;

  return !INVALID_DOC_VALUES.has(normalized);
};

const calculateProfileCompletion = ({ mover, hasApprovedInsurance, hasApprovedLicense }) => {
  let completed = 0;
  const total = 10; // Google profile link is optional and not counted

  if (mover.company_name) completed++;
  if (mover.company_description) completed++;
  if (mover.website_link) completed++;
  if (mover.company_logo) completed++;
  if (mover.phone) completed++;
  if (mover.email) completed++;
  if (mover.business_address) completed++;
  if (hasApprovedInsurance) completed++;
  if (hasApprovedLicense) completed++;
  if (mover.city && mover.state) completed++;

  return Math.round((completed / total) * 100);
};

const MoversList = () => {
  const [movers, setMovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const moversPerPage = 10;

  useEffect(() => {
    const fetchMovers = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('movers_data')
          .select('id, company_name, company_logo, city, state, years_in_business, is_verified, email, company_description, website_link, phone, business_address')
          .eq('is_active', true)
          .eq('account_paused', false);

        if (error) throw error;

        const suspensionCache = readSuspensionCache();
        const suspendedEmails = new Set(
          Object.entries(suspensionCache)
            .filter(([, isSuspended]) => Boolean(isSuspended))
            .map(([email]) => email)
        );

        try {
          const emails = (data || []).map((mover) => mover.email).filter(Boolean);

          if (emails.length > 0) {
            const response = await fetch(`${ADMIN_API_BASE}/get-mover-ban-status`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ emails }),
            });

            if (response.ok) {
              const result = await response.json();

              if (result.success && result.banStatus) {
                const updatedCache = { ...suspensionCache };

                Object.entries(result.banStatus).forEach(([email, status]) => {
                  const isSuspended = Boolean(status?.banned);
                  updatedCache[email] = isSuspended;

                  if (isSuspended) {
                    suspendedEmails.add(email);
                  } else {
                    suspendedEmails.delete(email);
                  }
                });

                writeSuspensionCache(updatedCache);
              }
            }
          }
        } catch {
        }

        const visibleMovers = (data || []).filter((mover) => !suspendedEmails.has(mover.email));
        const visibleMoverIds = visibleMovers.map((mover) => mover.id);
        let approvedInsuranceByMover = new Set();
        let approvedLicenseByMover = new Set();

        if (visibleMoverIds.length > 0) {
          const [{ data: insuranceData, error: insuranceError }, { data: licenseData, error: licenseError }] = await Promise.all([
            supabase
              .from('mover_insurance')
              .select('mover_id, policy_number')
              .in('mover_id', visibleMoverIds)
              .eq('status', 'approved'),
            supabase
              .from('mover_licenses')
              .select('mover_id, license_number')
              .in('mover_id', visibleMoverIds)
              .eq('status', 'approved')
          ]);

          if (insuranceError) throw insuranceError;
          if (licenseError) throw licenseError;

          approvedInsuranceByMover = new Set(
            (insuranceData || [])
              .filter((item) => hasMeaningfulDocumentValue(item.policy_number))
              .map((item) => item.mover_id)
          );

          approvedLicenseByMover = new Set(
            (licenseData || [])
              .filter((item) => hasMeaningfulDocumentValue(item.license_number))
              .map((item) => item.mover_id)
          );
        }

        const profileCompleteMovers = visibleMovers.filter((mover) => {
          const hasApprovedInsurance = approvedInsuranceByMover.has(mover.id);
          const hasApprovedLicense = approvedLicenseByMover.has(mover.id);

          if (!hasApprovedInsurance || !hasApprovedLicense) {
            return false;
          }

          const completion = calculateProfileCompletion({
            mover,
            hasApprovedInsurance,
            hasApprovedLicense,
          });

          return completion === 100;
        });

        const moverIds = profileCompleteMovers.map((mover) => mover.id);
        let reviewsByMover = {};

        if (moverIds.length > 0) {
          const { data: reviewsData, error: reviewsError } = await supabase
            .from('mover_reviews')
            .select('mover_id, rating')
            .in('mover_id', moverIds)
            .eq('is_approved', true);

          if (reviewsError) throw reviewsError;

          reviewsByMover = (reviewsData || []).reduce((accumulator, review) => {
            const current = accumulator[review.mover_id] || { sum: 0, count: 0 };
            current.sum += review.rating;
            current.count += 1;
            accumulator[review.mover_id] = current;
            return accumulator;
          }, {});
        }

        const moversWithRatings = profileCompleteMovers.map((mover) => {
          const ratingData = reviewsByMover[mover.id] || { sum: 0, count: 0 };
          const avgRating = ratingData.count > 0 ? ratingData.sum / ratingData.count : 0;

          return {
            ...mover,
            avgRating,
            reviewCount: ratingData.count,
            image: mover.company_logo || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400',
            completedMoves: createStableCompletedMoves(mover),
          };
        });

        // Sort by avgRating (descending) and then by reviewCount (descending)
        const sortedMovers = moversWithRatings.sort((a, b) => {
          if (b.avgRating !== a.avgRating) {
            return b.avgRating - a.avgRating;
          }
          return b.reviewCount - a.reviewCount;
        });

        setMovers(sortedMovers);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchMovers();
  }, []);

  // Pagination Logic
  const indexOfLastMover = currentPage * moversPerPage;
  const indexOfFirstMover = indexOfLastMover - moversPerPage;
  const currentMovers = movers.slice(indexOfFirstMover, indexOfLastMover);
  const totalPages = Math.ceil(movers.length / moversPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Helmet>
        <title>Find Professional Movers – MyMoveAdvisor</title>
        <meta name="description" content="Browse verified professional moving companies near you. Compare ratings, reviews, and services to find the best mover for your needs." />
        <link rel="canonical" href="https://mymoveadvisor.com/movers" />
        <meta property="og:title" content="Find Professional Movers – MyMoveAdvisor" />
        <meta property="og:description" content="Browse verified professional moving companies. Compare ratings and reviews." />
        <meta property="og:url" content="https://mymoveadvisor.com/movers" />
      </Helmet>
      {/* Premium Header */}}
      <div className="bg-primary pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2000" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6">Our Mover Network</h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto">
            Discover the highest-rated professional movers in your area. Verified for quality and reliability.
          </p>
        </div>
      </div>

      {/* Grid */}
      <div className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white rounded-3xl shadow-sm border border-gray-100 animate-pulse h-[450px]"></div>
            ))}
          </div>
        ) : currentMovers.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
            <div className="text-6xl mb-6">📦</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No movers available</h3>
            <p className="text-gray-500">We couldn't find any movers at this time.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {currentMovers.map((mover) => (
              <div
                key={mover.id}
                className="bg-white rounded-3xl shadow-[0_4px_20px_rgb(0,0,0,0.05)] hover:shadow-[0_32px_64px_rgb(0,0,0,0.12)] transition-all duration-500 overflow-hidden border border-gray-100/80 transform hover:-translate-y-2 group flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={mover.image}
                    alt={mover.company_name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    {mover.is_verified && (
                      <div className="bg-green-500 text-white text-[10px] px-3 py-1.5 rounded-full font-bold uppercase tracking-wider flex items-center shadow-lg border border-white/20 backdrop-blur-sm">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">{mover.company_name}</h3>
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {mover.city}, {mover.state}
                  </div>

                  <div className="flex items-center mb-4">
                    <div className="flex items-center bg-orange-50 px-2 py-1 rounded-lg">
                      <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-primary font-bold text-sm ml-1">{mover.avgRating > 0 ? mover.avgRating.toFixed(1) : 'New'}</span>
                    </div>
                    <span className="text-gray-400 text-xs ml-2 font-medium">({mover.reviewCount} Reviews)</span>
                  </div>

                  <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider">Experience</span>
                      <span className="text-sm font-bold text-gray-700">{mover.years_in_business || 0} Years</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider">Completed</span>
                      <span className="text-sm font-bold text-gray-700">{mover.completedMoves}+</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div className="container mx-auto px-4 pb-24 flex justify-center items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-3 rounded-xl border border-gray-200 transition-all ${
              currentPage === 1 
              ? 'opacity-50 cursor-not-allowed text-gray-300' 
              : 'hover:bg-primary hover:text-white text-gray-600 hover:border-primary shadow-sm'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => handlePageChange(i + 1)}
              className={`w-12 h-12 rounded-xl font-bold transition-all shadow-sm ${
                currentPage === i + 1
                ? 'bg-primary text-white scale-110 shadow-primary/30'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100'
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-3 rounded-xl border border-gray-200 transition-all ${
              currentPage === totalPages 
              ? 'opacity-50 cursor-not-allowed text-gray-300' 
              : 'hover:bg-primary hover:text-white text-gray-600 hover:border-primary shadow-sm'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default MoversList;
